import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Scale, Plus, Trash2, PlayCircle, Sparkles } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

export default function MyRecipes() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [recipes, setRecipes] = useState([]);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user || user === false) { nav("/login"); return; }
    api.get("/recipes/mine").then((r) => setRecipes(r.data));
  }, [user, loading, nav]);

  const del = async (id) => {
    if (!window.confirm("Delete this recipe? This can't be undone.")) return;
    setBusy(id);
    try {
      await api.delete(`/recipes/${id}`);
      setRecipes((prev) => prev.filter((r) => r.id !== id));
      toast.success("Deleted");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(null);
  };

  if (loading || !user) return null;

  return (
    <>
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 lg:py-10">
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#857163]">
              <Scale className="w-3.5 h-3.5" style={{ color: "#8EAC50" }} />
              <span>Your saved formulas</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#3B2C24] mt-1">
              My <span style={{ color: "#8EAC50" }}>Recipes</span>
            </h1>
            <p className="mt-2 text-[#857163]">Your saved formulas from the Recipe Scaler. Open any to instantly re-scale it.</p>
          </div>
          <Link
            to="/scaler"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-white font-semibold text-sm hover:opacity-95 transition shrink-0"
            style={{ background: "#8EAC50" }}
            data-testid="new-recipe-btn"
          ><Plus className="w-4 h-4" /> New recipe</Link>
        </div>

        {recipes.length === 0 ? (
          <div className="cp-card p-10 text-center">
            <div className="font-heading text-2xl text-[#3B2C24] mb-2">No saved recipes yet</div>
            <p className="text-[#857163] mb-4">Save a recipe from the Scaler to build your personal cookbook.</p>
            <Link
              to="/scaler"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-white font-semibold text-sm"
              style={{ background: "#8EAC50" }}
            ><Scale className="w-4 h-4" /> Open the Scaler</Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {recipes.map((r) => {
              const flour = (r.ingredients || []).find((i) => i.is_flour);
              const flourG = flour ? (parseFloat(flour.amount) || 0) * (flour.unit === "kg" ? 1000 : 1) : 0;
              return (
                <div key={r.id} className="cp-card p-5 flex flex-col" data-testid={`recipe-${r.id}`}>
                  <h3 className="font-heading text-xl text-[#3B2C24] leading-tight">{r.name}</h3>
                  <div className="text-xs text-[#857163] mt-1">
                    {r.ingredients?.length || 0} ingredients · yields {r.servings}
                  </div>
                  {flourG > 0 && (
                    <div className="mt-2 text-xs text-[#3B2C24]">
                      <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: "#8EAC50" }} /> {flour.name || "flour"}: <b>{flourG}g</b></span>
                    </div>
                  )}
                  <ul className="mt-2 text-xs text-[#857163] space-y-0.5 line-clamp-4">
                    {(r.ingredients || []).slice(0, 4).map((i, idx) => (
                      <li key={idx} className="truncate">• {i.name || "—"} · {i.amount}{i.unit}</li>
                    ))}
                  </ul>
                  <div className="mt-auto pt-4 flex items-center justify-between gap-2">
                    <div className="text-[10px] text-[#857163]">
                      {(() => { try { return `Updated ${formatDistanceToNow(new Date(r.updated_at), { addSuffix: true })}`; } catch { return ""; } })()}
                    </div>
                    <div className="flex gap-1.5">
                      <Link
                        to={`/scaler?load=${r.id}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold text-white hover:opacity-90 transition"
                        style={{ background: "#8EAC50" }}
                        data-testid={`load-${r.id}`}
                      ><PlayCircle className="w-3 h-3" /> Open</Link>
                      <button
                        onClick={() => del(r.id)}
                        disabled={busy === r.id}
                        className="p-1.5 rounded-full text-red-500 hover:bg-red-50 border border-red-200 disabled:opacity-50"
                        data-testid={`delete-${r.id}`}
                      ><Trash2 className="w-3 h-3" /></button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </>
  );
}
